/* eslint-disable */

import { useState } from 'react'
import { format } from 'date-fns'
import { ChevronUp, ChevronDown, RefreshCw } from 'lucide-react'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../Components/ui/card'
import { Checkbox } from '../../Components/ui/checkbox'
import { Button } from '../../Components/ui/button'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../Components/ui/table'
import { Badge } from '../../Components/ui/badge'
import { useRecoilValueLoadable, useRecoilRefresher_UNSTABLE } from 'recoil'
import { FetchOtmOrders } from '../../store/AdminStore'
import { formatIndianCurrency, toCurrency } from '../../utils/CurrencyUtil'
import { Input } from '../../Components/ui/input'
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '../../Components/ui/accordian'
import { CalendarDatePicker } from '../../Components/ui/calendar-date-picker'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../Components/ui/select'
import { patch } from '../../http/restservice'

const getStatusText = (orderStatus) => {
    switch (orderStatus) {
        case 'DONE':
            return 'Done'
        case 'ACTIVE':
            return 'Active'
        case 'ORDER_PLACED':
            return 'Order Placed'
        case 'FAILURE':
            return 'Failure'
        default:
            return orderStatus || 'Pending'
    }
}

const getStatusCellClass = (orderStatus) => {
    switch (orderStatus) {
        case 'DONE':
            return 'bg-secondary-50'
        case 'ACTIVE':
            return 'bg-warning-50'
        case 'ORDER_PLACED':
            return 'bg-warning-50'
        case 'FAILURE':
            return 'bg-error-50'
        default:
            return ''
    }
}

const getGroupTriggerClass = (orders) => {
    if (!orders || orders.length === 0) return 'bg-secondary-50'

    // Priority: FAILURE > ACTIVE/ORDER_PLACED > DONE > default
    if (orders.some((o) => o?.orderStatus === 'FAILURE')) return 'bg-error-50'
    if (orders.some((o) => o?.orderStatus === 'ACTIVE' || o?.orderStatus === 'ORDER_PLACED')) {
        return 'bg-warning-50'
    }
    if (orders.some((o) => o?.orderStatus === 'DONE')) return 'bg-secondary-50'

    return 'bg-secondary-50'
}

const getStatusBadge = (orderStatus) => {
    let colorClass
    const statusText = getStatusText(orderStatus)

    switch (orderStatus) {
        case 'DONE':
            colorClass = 'bg-secondary-100 text-secondary-800 hover:bg-secondary-100'
            break
        case 'ACTIVE':
            colorClass = 'bg-warning-100 text-warning-800 hover:bg-warning-100'
            break
        case 'ORDER_PLACED':
            colorClass = 'bg-warning-100 text-warning-800 hover:bg-warning-100'
            break
        case 'FAILURE':
            colorClass = 'bg-error-100 text-error-800 hover:bg-error-100'
            break
        default:
            colorClass = 'bg-secondary-100 text-secondary-800 hover:bg-secondary-100'
    }

    return (
        <Badge className={colorClass}>
            {statusText}
        </Badge>
    )
}

const GroupedOrders = ({ groupedOrders, sortColumn, sortDirection, onSort, selectedOrders, onOrderSelect, onGroupSelect }) => {
    const getSortIcon = (column) => {
        if (sortColumn !== column) {
            return <ChevronUp className='w-3 h-3 opacity-30' />
        }
        return sortDirection === 'asc' ? (
            <ChevronUp className='w-3 h-3' />
        ) : (
            <ChevronDown className='w-3 h-3' />
        )
    }

    const handleSort = (column) => {
        if (onSort) {
            onSort(column)
        }
    }

    const totalAmount = (orders) => {
        return orders?.reduce((acc, order) => acc + Number(order?.amount || 0), 0)
    }

    const getStatusCounts = (orders) => {
        const counts = {}
        orders?.forEach((order) => {
            const status = order?.orderStatus || 'UNKNOWN'
            counts[status] = (counts[status] || 0) + 1
        })
        return counts
    }

    return (
        <>
            {/* Header Row - Hidden on mobile */}
            <div className='hidden md:block w-full px-2 py-3 bg-secondary-100 rounded-md mb-2'>
                <div className='flex items-center gap-4'>
                    <span className='font-semibold text-secondary-700 text-xs md:text-sm min-w-[40px]'>
                        Select
                    </span>
                    <span className='font-semibold text-secondary-700 text-xs md:text-sm flex-1 min-w-[150px]'>
                        Client Code
                    </span>
                    <span className='font-semibold text-secondary-700 text-xs md:text-sm flex-1 min-w-[150px]'>
                        Client Name
                    </span>
                    <span className='font-semibold text-secondary-700 text-xs md:text-sm flex-1 min-w-[200px]'>
                        Basket Name
                    </span>
                    <span
                        className='font-semibold text-secondary-700 text-xs md:text-sm text-right min-w-[120px] cursor-pointer hover:text-secondary-900 flex items-center gap-1 select-none'
                        onClick={() => handleSort('totalAmount')}
                    >
                        Total Amount
                        {getSortIcon('totalAmount')}
                    </span>

                    <span className='font-semibold text-secondary-700 text-xs md:text-sm text-right min-w-[150px]'>
                        Status
                    </span>
                </div>
            </div>

            <Accordion type='multiple' className='w-full space-y-2'>
                {groupedOrders.map((group, groupIndex) => {
                    const statusCounts = getStatusCounts(group)
                    const triggerBgClass = getGroupTriggerClass(group)
                    const groupOrderIds = group.map((order) => order.id)
                    const allGroupSelected = groupOrderIds.every((id) => selectedOrders.includes(id))
                    const someGroupSelected = groupOrderIds.some((id) => selectedOrders.includes(id))

                    const handleGroupCheckboxChange = (checked) => {
                        if (onGroupSelect) {
                            onGroupSelect(groupOrderIds, checked)
                        }
                    }

                    return (
                        <AccordionItem key={groupIndex} value={`group-${groupIndex}`} className='border-none'>
                            <AccordionTrigger
                                className={`${triggerBgClass} px-2 md:px-4 py-3 rounded-md border-none cursor-pointer`}
                            >
                                <div className='flex flex-col text-left space-y-2 w-full'>
                                    <div className='flex flex-col md:flex-row md:items-center gap-2 md:gap-4'>
                                        <div
                                            className='min-w-[40px] flex items-center'
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <Checkbox
                                                checked={allGroupSelected}
                                                onCheckedChange={handleGroupCheckboxChange}
                                                className='data-[state=checked]:bg-primary-500'
                                            />
                                        </div>
                                        <span className='font-semibold text-sm md:text-base flex-1 md:min-w-[150px]'>
                                            {group[0]?.clientCode}
                                        </span>
                                        <span className='font-semibold text-sm md:text-base flex-1 md:min-w-[150px]'>
                                            {group[0]?.clientName}
                                        </span>
                                        <span className='font-medium text-xs md:text-sm flex-1 md:min-w-[200px]'>
                                            {group[0]?.customerBasketName} ({group.length} Order{group.length > 1 ? 's' : ''})
                                        </span>
                                        <span className='text-xs md:text-sm text-muted-foreground md:min-w-[120px] text-right font-semibold'>
                                            ₹ {formatIndianCurrency(toCurrency(totalAmount(group)))}
                                        </span>
                                        <span className='text-xs md:text-sm text-right md:min-w-[150px]'>
                                            {Object.entries(statusCounts).map(([status, count]) => (
                                                <span key={status} className='mr-2'>
                                                    {getStatusText(status)}: {count}
                                                </span>
                                            ))}
                                        </span>
                                    </div>
                                </div>
                            </AccordionTrigger>
                            <AccordionContent className='space-y-3 pt-2'>
                                <div className='overflow-x-auto -mx-2 md:mx-0'>
                                    <Table className='min-w-[600px]'>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead className='text-xs md:text-sm w-[40px]'>Select</TableHead>
                                                <TableHead className='text-xs md:text-sm'>Order Number</TableHead>
                                                <TableHead className='text-xs md:text-sm'>Basket ID</TableHead>
                                                <TableHead className='text-xs md:text-sm'>Basket Name</TableHead>
                                                <TableHead className='text-right text-xs md:text-sm'>Amount</TableHead>
                                                <TableHead className='text-xs md:text-sm'>Order Status</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {group.map((order) => {
                                                const isSelected = selectedOrders.includes(order.id)

                                                return (
                                                    <TableRow key={order.id}>
                                                        <TableCell className='w-[40px]'>
                                                            <Checkbox
                                                                checked={isSelected}
                                                                onCheckedChange={(checked) => {
                                                                    if (onOrderSelect) {
                                                                        onOrderSelect(order.id, checked)
                                                                    }
                                                                }}
                                                                className='data-[state=checked]:bg-primary-500'
                                                            />
                                                        </TableCell>
                                                        <TableCell className='font-semibold text-xs md:text-sm'>
                                                            {order.orderNumber}
                                                        </TableCell>
                                                        <TableCell className='text-xs md:text-sm'>
                                                            {order.customerBasketInvestmentId}
                                                        </TableCell>
                                                        <TableCell className='text-xs md:text-sm'>
                                                            {order.customerBasketName}
                                                        </TableCell>
                                                        <TableCell className='text-right font-medium text-xs md:text-sm'>
                                                            ₹ {formatIndianCurrency(toCurrency(order?.amount || 0))}
                                                        </TableCell>
                                                        <TableCell className={getStatusCellClass(order.orderStatus)}>
                                                            {getStatusBadge(order.orderStatus)}
                                                        </TableCell>
                                                    </TableRow>
                                                )
                                            })}
                                        </TableBody>
                                    </Table>
                                </div>
                            </AccordionContent>
                        </AccordionItem>
                    )
                })}
            </Accordion>
        </>
    )
}

const OtmOrders = () => {
    const [selectedDateRange, setSelectedDateRange] = useState(() => {
        const today = new Date()
        const sevenDaysAgo = new Date()
        sevenDaysAgo.setDate(today.getDate() - 7)

        return {
            from: sevenDaysAgo,
            to: today,
        }
    })

    const [searchQuery, setSearchQuery] = useState('')
    const [sortColumn, setSortColumn] = useState(null)
    const [sortDirection, setSortDirection] = useState('asc')
    const [statusFilter, setStatusFilter] = useState('all')
    const [selectedOrders, setSelectedOrders] = useState([])
    const [newOrderStatus, setNewOrderStatus] = useState('DONE')
    const [isUpdating, setIsUpdating] = useState(false)

    const fromDateStr = format(selectedDateRange.from, 'yyyy-MM-dd')
    const toDateStr = format(selectedDateRange.to, 'yyyy-MM-dd')

    const otmOrdersSelector = FetchOtmOrders({ fromDateStr, toDateStr })
    const { state, contents: orders } = useRecoilValueLoadable(otmOrdersSelector)
    const refreshOtmOrders = useRecoilRefresher_UNSTABLE(otmOrdersSelector)

    // Filter orders by search query (clientCode or clientName)
    const filteredOrders =
        searchQuery && state === 'hasValue'
            ? orders?.data?.filter((order) => {
                const query = searchQuery.toLowerCase()
                return (
                    order.clientCode?.toLowerCase()?.includes(query) ||
                    order.clientName?.toLowerCase()?.includes(query)
                )
            })
            : orders?.data

    function calculateTotalAmount(group) {
        return group?.reduce((acc, order) => acc + Number(order?.amount || 0), 0)
    }

    function hasStatus(group, status) {
        return group?.some((order) => order?.orderStatus === status)
    }

    function filterGroupedOrders(groups, filter) {
        if (filter === 'all') {
            return groups
        }
        return groups.filter((group) => hasStatus(group, filter))
    }

    function groupOrders(orders) {
        if (!orders || orders.length === 0) return []

        const groups = []
        const groupMap = new Map()

        for (let order of orders) {
            // Group by clientCode and customerBasketInvestmentId
            const groupKey = `${order.clientCode}_${order.customerBasketInvestmentId}`

            if (groupMap.has(groupKey)) {
                groupMap.get(groupKey).push(order)
            } else {
                const newGroup = [order]
                groupMap.set(groupKey, newGroup)
                groups.push(newGroup)
            }
        }

        return groups
    }

    function sortGroupedOrders(groups, column, direction) {
        if (!column) return groups

        const sorted = [...groups].sort((a, b) => {
            let comparison = 0

            switch (column) {
                case 'totalAmount':
                    comparison = calculateTotalAmount(a) - calculateTotalAmount(b)
                    break
                case 'clientCode':
                    comparison = (a[0]?.clientCode || '').localeCompare(b[0]?.clientCode || '')
                    break
                case 'clientName':
                    comparison = (a[0]?.clientName || '').localeCompare(b[0]?.clientName || '')
                    break
                default:
                    return 0
            }

            return direction === 'asc' ? comparison : -comparison
        })

        return sorted
    }

    const handleSort = (column) => {
        if (sortColumn === column) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
        } else {
            setSortColumn(column)
            setSortDirection('asc')
        }
    }

    // Get unique order statuses for filter dropdown
    const uniqueStatuses = state === 'hasValue' && orders?.data
        ? [...new Set(orders.data.map((order) => order.orderStatus).filter(Boolean))]
        : []

    // Handler for selecting/deselecting individual orders
    const handleOrderSelect = (orderId, checked) => {
        if (checked) {
            setSelectedOrders((prev) => [...prev, orderId])
        } else {
            setSelectedOrders((prev) => prev.filter((id) => id !== orderId))
        }
    }

    // Handler for selecting/deselecting all orders in a group
    const handleGroupSelect = (orderIds, checked) => {
        if (checked) {
            setSelectedOrders((prev) => {
                const newSelected = [...prev]
                orderIds.forEach((id) => {
                    if (!newSelected.includes(id)) {
                        newSelected.push(id)
                    }
                })
                return newSelected
            })
        } else {
            setSelectedOrders((prev) => prev.filter((id) => !orderIds.includes(id)))
        }
    }

    // Handler for updating selected orders
    const handleUpdateOrders = async () => {
        if (selectedOrders.length === 0) {
            alert('Please select at least one order to update')
            return
        }

        setIsUpdating(true)
        try {
            // Get the full order objects for selected IDs
            const ordersToUpdate = filteredOrders
                ?.filter((order) => selectedOrders.includes(order.id))
                .map((order) => ({
                    id: order.id,
                    orderStatus: newOrderStatus,
                    orderNumber: order.orderNumber,
                    clientCode: order.clientCode,
                    clientName: order.clientName,
                    customerBasketInvestmentId: order.customerBasketInvestmentId,
                    amount: order.amount,
                    customerBasketName: order.customerBasketName,
                }))

            const response = await patch(
                'mutualfundservice/api/mutualFund/customerOTMOrders',
                ordersToUpdate,
            )

            if (response?.status === 'SUCCESS' || response?.success) {
                alert(`Successfully updated ${selectedOrders.length} order(s)`)
                setSelectedOrders([])
                // Refresh the OTM orders data
                refreshOtmOrders()
            } else {
                alert(`Failed to update orders: ${response?.message || 'Unknown error'}`)
            }
        } catch (error) {
            alert(`Error updating orders: ${error.message || 'Unknown error'}`)
        } finally {
            setIsUpdating(false)
        }
    }

    return (
        <div className='mx-auto p-2 md:p-6 space-y-4 md:space-y-6'>
            <div className='flex items-center justify-between'>
                <div>
                    <h1 className='text-xl md:text-2xl lg:text-3xl font-bold tracking-tight'>OTM Orders</h1>
                    <p className='text-muted-foreground text-xs md:text-sm'>
                        Manage and track all One Time Mandate orders for mutual fund investments
                    </p>
                </div>
            </div>

            <Card>
                <CardHeader className='px-3 md:px-6'>
                    <CardTitle className='text-base md:text-lg'>Filter OTM Orders</CardTitle>
                    <CardDescription className='text-xs md:text-sm'>
                        Select date range to fetch OTM orders. Default shows last 7 days data.
                    </CardDescription>
                </CardHeader>
                <CardContent className='space-y-4'>
                    <div className='flex flex-col sm:flex-row gap-4 items-end'>
                        <div className='grid gap-2'>
                            <label className='text-sm font-medium'>Select Date Range</label>
                            <CalendarDatePicker date={selectedDateRange} onDateSelect={setSelectedDateRange} />
                        </div>
                    </div>

                    <div className='flex flex-col sm:flex-row gap-4 items-end'>
                        {orders?.data?.length && (
                            <div className='grid gap-2'>
                                <label htmlFor='search' className='text-sm font-medium'>
                                    Search by Client Code or Name
                                </label>
                                <Input
                                    id='search'
                                    placeholder='Enter client code or name'
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className='w-[280px]'
                                />
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {state === 'hasError' && (
                <Card className='border-destructive'>
                    <CardContent className='pt-6'>
                        <div className='flex items-center gap-2 text-destructive'>
                            <span className='font-medium'>Error:</span>
                            <span>Something went wrong, please try again later</span>
                        </div>
                    </CardContent>
                </Card>
            )}

            <Card>
                <CardHeader className='px-3 md:px-6'>
                    <div className='flex items-center justify-between gap-4'>
                        <div className='flex-1'>
                            <CardTitle className='text-base md:text-lg'>
                                OTM Orders (
                                {filteredOrders?.length > 0
                                    ? filterGroupedOrders(groupOrders(filteredOrders), statusFilter)?.length || 0
                                    : 0}
                                )
                            </CardTitle>
                            <CardDescription className='text-xs md:text-sm'>
                                {selectedDateRange.from && selectedDateRange.to && (
                                    <>
                                        Showing orders from {format(selectedDateRange.from, 'PPP')} to{' '}
                                        {format(selectedDateRange.to, 'PPP')}
                                    </>
                                )}
                            </CardDescription>
                        </div>
                        <div className='flex items-center gap-2'>
                            <Select value={statusFilter} onValueChange={setStatusFilter}>
                                <SelectTrigger className='w-[180px] border'>
                                    <SelectValue placeholder='Filter by status' />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value='all'>All Statuses</SelectItem>
                                    {uniqueStatuses.map((status) => (
                                        <SelectItem key={status} value={status}>
                                            {getStatusText(status)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                </CardHeader>

                <CardContent className='px-3 md:px-6'>
                    {selectedOrders.length > 0 && (
                        <div className='mb-4 p-4 bg-primary-50 border border-primary-200 rounded-lg flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4'>
                            <div className='flex flex-col sm:flex-row items-start sm:items-center gap-4'>
                                <span className='text-sm font-medium text-primary-900'>
                                    {selectedOrders.length} order(s) selected
                                </span>
                                <div className='flex items-center gap-2'>
                                    <label className='text-sm font-medium'>Update Status to:</label>
                                    <Select value={newOrderStatus} onValueChange={setNewOrderStatus}>
                                        <SelectTrigger className='w-[180px] border'>
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value='DONE'>DONE</SelectItem>
                                            <SelectItem value='ACTIVE'>ACTIVE</SelectItem>
                                            <SelectItem value='ORDER_PLACED'>ORDER PLACED</SelectItem>
                                            <SelectItem value='FAILURE'>FAILURE</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className='flex items-center gap-2'>
                                <Button
                                    variant='outline'
                                    onClick={() => setSelectedOrders([])}
                                    disabled={isUpdating}
                                >
                                    Clear Selection
                                </Button>
                                <Button
                                    onClick={handleUpdateOrders}
                                    disabled={isUpdating || selectedOrders.length === 0}
                                    className='bg-primary-500 hover:bg-primary-600'
                                >
                                    {isUpdating ? (
                                        <>
                                            <RefreshCw className='h-4 w-4 mr-2 animate-spin' />
                                            Updating...
                                        </>
                                    ) : (
                                        'Update Selected Orders'
                                    )}
                                </Button>
                            </div>
                        </div>
                    )}
                    {state === 'loading' ? (
                        <div className='flex items-center justify-center py-8'>
                            <RefreshCw className='h-8 w-8 animate-spin text-muted-foreground' />
                            <span className='ml-2 text-muted-foreground'>Loading OTM orders...</span>
                        </div>
                    ) : state === 'hasValue' &&
                        (orders?.message === 'No OTM orders found.' || !orders?.data?.length) ? (
                        <div className='text-center py-8 text-muted-foreground'>
                            {orders?.message || 'No OTM orders found'}
                        </div>
                    ) : (
                        <div>
                            {filteredOrders?.length > 0 ? (
                                <GroupedOrders
                                    groupedOrders={sortGroupedOrders(
                                        filterGroupedOrders(groupOrders(filteredOrders), statusFilter),
                                        sortColumn,
                                        sortDirection,
                                    )}
                                    sortColumn={sortColumn}
                                    sortDirection={sortDirection}
                                    onSort={handleSort}
                                    selectedOrders={selectedOrders}
                                    onOrderSelect={handleOrderSelect}
                                    onGroupSelect={handleGroupSelect}
                                />
                            ) : (
                                <div className='text-center py-8 text-muted-foreground'>No OTM orders found</div>
                            )}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    )
}

export default OtmOrders
